import { Pagination } from "@mui/material";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { AdminIcon } from "../../../assets/img/icons";
import Button from "../../../components/Button";
import ConnectedLayout from "../../../components/ConnectedLayout";
import Header from "../../../components/Header";
import Input from "../../../components/Input";
import { Select } from "../../../components/OldSelect";
import { AuthContext } from "../../../contexts/AuthContext";
import { getAllRoles } from "../../../services/api/admin/role";
import { fetchUserForMemberEdit } from "../../../services/api/admin/user";
import { MemberEditUser } from "../../../types/api/admin/user";
import { getAdminPayouts } from "../../../services/api/payout";
import { Role } from "../../../types/Role";
import { UserAdminPayoutsResult } from "../../../types/User";
import MemberModal from "../../Members/MemberModal";
import PayoutsTable from "./PayoutsTable";

import DatePicker from "../../../components/DatePicker";
import { PayoutsAdminSortBy } from "../../../types/api/admin/payouts";
import { getFormattedDateInput, parseInputDateToLocal } from "../../../utils/date";
import styles from "./styles.module.scss";

const AdminPayouts: FC = () => {
    const { hasPermission } = useContext(AuthContext)!;

    const [memberModalOpen, setMemberModalOpen] = useState(false);
    const [userToEdit, setUserToEdit] = useState<MemberEditUser | null>(null);
    const [roles, setRoles] = useState<Role[]>([]);
    const [usersRefreshKey, setUsersRefreshKey] = useState(0);

    const [loading, setLoading] = useState(true);

    const [userByPage, setUserByPage] = useState(10);
    const [users, setUsers] = useState<UserAdminPayoutsResult[]>([]);
    const [currentUsersPage, setCurrentUsersPage] = useState<
        UserAdminPayoutsResult[]
    >([]);
    const [page, setPage] = useState(1);
    const [maxPage, setMaxPage] = useState(1);
    const [showUserWithNoPayouts, setShowUserWithNoPayouts] = useState(false);
    const [sortBy, setSortBy] = useState<PayoutsAdminSortBy>("payout_total");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [fundingStatusSort, setFundingStatusSort] = useState<string>("");
    const [filtersHydrated, setFiltersHydrated] = useState(false);
    const restoredPageRef = useRef<number | null>(null);

    useEffect(() => {
        const raw = sessionStorage.getItem("adminPayoutsReturnFilters");
        if (raw) {
            try {
                const parsed = JSON.parse(raw) as {
                    searchInput?: string;
                    startDate?: string;
                    endDate?: string;
                    showUserWithNoPayouts?: boolean;
                    fundingStatusSort?: string;
                    sortBy?: PayoutsAdminSortBy;
                    sortOrder?: "asc" | "desc";
                    page?: number;
                    userByPage?: number;
                };

                if (parsed.searchInput != null) setSearchInput(parsed.searchInput);

                if (parsed.startDate) {
                    const d = parseInputDateToLocal(parsed.startDate);
                    if (d) setStartDate(d);
                }
                if (parsed.endDate) {
                    const d = parseInputDateToLocal(parsed.endDate);
                    if (d) setEndDate(d);
                }

                if (typeof parsed.showUserWithNoPayouts === "boolean") {
                    setShowUserWithNoPayouts(parsed.showUserWithNoPayouts);
                }
                if (parsed.fundingStatusSort != null) {
                    setFundingStatusSort(parsed.fundingStatusSort);
                }
                if (parsed.sortBy) setSortBy(parsed.sortBy);
                if (parsed.sortOrder) setSortOrder(parsed.sortOrder);
                if (typeof parsed.page === "number" && !isNaN(parsed.page)) {
                    setPage(parsed.page);
                    restoredPageRef.current = parsed.page;
                }
                if (parsed.userByPage && !isNaN(Number(parsed.userByPage))) {
                    setUserByPage(parsed.userByPage);
                }
            } catch {
                // Ignore corrupted payload
            } finally {
                sessionStorage.removeItem("adminPayoutsReturnFilters");
            }
        }
        setFiltersHydrated(true);
    }, []);

    // Met à jour les users affichés à chaque changement de users, page ou userByPage
    useEffect(() => {
        const start = (page - 1) * userByPage;
        const end = start + userByPage;
        setCurrentUsersPage(users.slice(start, end));
    }, [users, page, userByPage]);

    const canReadAdminFundingStatus = hasPermission("admin-funding-status", "read");
    // Map statut financé + capital depuis la réponse API (plus de requêtes par utilisateur)
    const fundingStatusMap = useMemo(() => {
        if (!canReadAdminFundingStatus) return {};
        const map: Record<
            string,
            { isFunded: boolean; fundedTotalBalance?: string } | null
        > = {};
        users.forEach((u) => {
            const balanceStr =
                u.fundedTotalBalance != null && String(u.fundedTotalBalance).trim() !== ""
                    ? String(u.fundedTotalBalance)
                    : undefined;
            if (u.isFunded != null) {
                map[u.id] = { isFunded: u.isFunded, fundedTotalBalance: balanceStr };
            } else if (balanceStr != null) {
                map[u.id] = { isFunded: true, fundedTotalBalance: balanceStr };
            } else {
                map[u.id] = null;
            }
        });
        return map;
    }, [users, canReadAdminFundingStatus]);

    const lastThirtyDays = new Date();
    lastThirtyDays.setDate(lastThirtyDays.getDate() - 30);

    const [searchInput, setSearchInput] = useState("");
    const [startDate, setStartDate] = useState(lastThirtyDays);
    const [endDate, setEndDate] = useState(new Date());

    useEffect(() => {
        if (hasPermission("admin-members", "update")) {
            getAllRoles().then(setRoles);
        }
    }, [hasPermission]);

    const handleEditProfile = async (user: UserAdminPayoutsResult) => {
        try {
            const editUser = await fetchUserForMemberEdit(user.id, roles);
            if (!editUser) {
                toast.error(
                    "Profil incomplet (email, rôle ou statut manquant). Vérifiez la réponse GET /v3/admin/users/:id.",
                );
                return;
            }
            setUserToEdit(editUser);
            setMemberModalOpen(true);
        } catch {
            toast.error("Impossible de charger le profil du membre.");
        }
    };

    const fundingStatusFilterParam: "" | "funded" | "not_funded" | undefined =
        fundingStatusSort === "funded"
            ? "funded"
            : fundingStatusSort === "notFunded"
            ? "not_funded"
            : undefined;

    useEffect(() => {
        if (!filtersHydrated) return;
        setLoading(true);
        getAdminPayouts(
            searchInput,
            getFormattedDateInput(startDate),
            getFormattedDateInput(endDate),
            sortBy,
            sortOrder,
            showUserWithNoPayouts,
            fundingStatusFilterParam
        )
            .then((body) => {
                if (restoredPageRef.current != null) {
                    setPage(restoredPageRef.current);
                    restoredPageRef.current = null;
                } else {
                    setPage(1);
                }
                setUsers(body?.users ?? []);
            })
            .catch(() => {
                setUsers([]);
            })
            .finally(() => setLoading(false));
    }, [
        filtersHydrated,
        searchInput,
        startDate,
        endDate,
        sortBy,
        sortOrder,
        showUserWithNoPayouts,
        fundingStatusFilterParam,
        usersRefreshKey,
    ]);

    useEffect(() => {
        const newMaxPage = Math.max(1, Math.ceil(users.length / userByPage));
        setMaxPage(newMaxPage);
        if (page > newMaxPage) {
            setPage(1);
        }
    }, [users, userByPage, page]);

    return (
        <ConnectedLayout>
            <MemberModal
                open={memberModalOpen}
                handleClose={() => {
                    setMemberModalOpen(false);
                    setUserToEdit(null);
                }}
                user={userToEdit ?? undefined}
                roles={roles}
                onUserUpdated={() => setUsersRefreshKey((key) => key + 1)}
            />
            <Header icon={AdminIcon} title="Les payouts" />
            <div className={styles.header}>
                <div className={styles.filters}>
                    <div className={styles.filters_row}>
                        <div className={styles.filterField}>
                           
                            <Input
                                value={searchInput}
                                onChanged={setSearchInput}
                                className={styles.filters_input}
                                type="text"
                                placeholder="Rechercher un membre"
                            />
                        </div>
                        <div className={styles.filters_divider} aria-hidden="true" />
                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Financement
                            </span>
                            <Select
                                value={fundingStatusSort}
                                onChanged={(value) => {
                                    const next = Array.isArray(value)
                                        ? value[0] ?? ""
                                        : value;
                                    setFundingStatusSort(next);
                                }}
                                className={`${styles.pagination_select} ${styles.fundingSelect}`}
                                mobileAriaLabel="Financement"
                            >
                                <option value="">-</option>
                                <option value="funded">Financé</option>
                                <option value="notFunded">Non financé</option>
                            </Select>
                        </div>
                        <div className={styles.filters_divider} aria-hidden="true" />
                        <div className={styles.filterField}>
                            <span className={styles.filterLabel}>
                                Période
                            </span>
                            <div className={styles.dates_switchs}>
                                <DatePicker
                                    mode="range"
                                    startDate={startDate}
                                    setStartDate={setStartDate}
                                    endDate={endDate}
                                    setEndDate={setEndDate}
                                    classNameTrigger={styles.date_picker_trigger}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <Button
                    className={styles.noPayoutsButton}
                    onClick={() => setShowUserWithNoPayouts((v) => !v)}
                    size="small"
                    variant="secondary"
                    label={showUserWithNoPayouts ? "Payouts uniquement" : "Afficher membres sans payouts"}
                />
            </div>
            <PayoutsTable
                isLoading={loading}
                users={currentUsersPage}
                userByPage={userByPage}
                page={page}
                returnFilters={{
                    searchInput,
                    startDate: getFormattedDateInput(startDate),
                    endDate: getFormattedDateInput(endDate),
                    showUserWithNoPayouts,
                    fundingStatusSort,
                    sortBy,
                    sortOrder,
                    page,
                    userByPage,
                }}
                fundingStatusMap={canReadAdminFundingStatus ? fundingStatusMap : undefined}
                showFundingColumn={canReadAdminFundingStatus}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortByPayoutDesc={() => {
                    setSortBy("payout_total");
                    setSortOrder("desc");
                }}
                onSortByCapital={(order: "asc" | "desc") => {
                    setSortBy("fundedTotalBalance");
                    setSortOrder(order);
                }}
                onEditProfile={handleEditProfile}
            />
            {users.length > 0 && (
                <div className={styles.payouts_pagination_container}>
                    <Pagination
                        count={maxPage}
                        onChange={(e, value) => setPage(value)}
                        page={page}
                    />
                    <Select
                        value={userByPage.toString()}
                        onChanged={(value) => {
                            setUserByPage(Number(value));
                            setPage(1);
                        }}
                        className={styles.pagination_select}
                    >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                    </Select>
                </div>
            )}
        </ConnectedLayout>
    );
};

export default AdminPayouts;
